import React, { useEffect, useReducer, useState } from 'react'

import moment from 'moment'
import styled from 'styled-components'

import {
  createScheduleManualSequence,
  createNewSchedule,
  createSchedulePointMapping,
  fetchScheduleManualSequence,
  saveSchedule,
  updatePoint,
} from '../../../../../../api/adminBuildingFetches'
import { ProjectFloorSection } from '../../../../../../api/types'
import iconDown from '../../../../../../assets/images/icon_down_gray.svg'
import { useBuildingContext } from '../../../../../../contexts/buildingContext'
import { useNotifications } from '../../../../../../contexts/notificationProvider'
import { useNavigation } from '../../../../../../hooks/useNavigation'
import { ConfirmationModal } from '../../../../../common/Confirmation/Confirmation'
import { useFetchProjectFloorSectionsQuery } from '../../../hooks/adminBuildingQueries'
import { IMapPoint } from '../../ManagePoints/ManagedMapPoint'
import { useManageSchedulesContext } from '../ManageSchedulesContext'
import { getNextScan, getTimePieces } from '../ScheduleHelperFunctions'

import {
  commonInputStyles,
  invalidInputStyle,
  ScheduleControlsDatePicker,
} from './components/ScheduleControlsDatePicker'
import { ScheduleControlsScanInformation } from './components/ScheduleControlsScanInformation'
import { ScheduleControlsStatistics } from './components/ScheduleControlsStatistics'

export enum Frequency {
  never = 'Frequency',
  once = 'once',
  weekly = 'weekly',
  monthly = 'monthly',
}

const formatDateForSubmit = (date: Date | undefined): string => {
  return moment(date).format('YYYY-MM-DD')
}

const pointIdsForSection = (
  points: IMapPoint[],
  sectionId: number,
): number[] => {
  return points
    .filter((point: IMapPoint) => point.project_floor_section === sectionId)
    .map((point: IMapPoint) => point.point_id)
}

interface FieldValidations {
  name: boolean
  start_date: boolean
  till_date: boolean
  time: boolean
  frequency: boolean
}

const initialFieldValidations = {
  name: true,
  start_date: true,
  till_date: true,
  time: true,
  frequency: true,
}

const fieldValidationsReducer = (
  state: FieldValidations,
  stateUpdates: Partial<FieldValidations>,
) => {
  return { ...state, ...stateUpdates }
}

interface IScheduleControlsProps {
  setScheduleSaving: (newValue: boolean) => void
  manualSequence: number[]
  setManualSequence: (
    sequence: number[] | ((prev: number[]) => number[]),
  ) => void
  loadManualSequence: (sequence: number[]) => void
  markManualSequenceSaved: () => void
  manualSequenceDirty: boolean
  isSequencingMode: boolean
  setIsSequencingMode: () => void
  selectedSectionId: number | null
  setSelectedSectionId: React.Dispatch<React.SetStateAction<number | null>>
  sections: ProjectFloorSection[]
  setSections: React.Dispatch<React.SetStateAction<ProjectFloorSection[]>>
  scheduledPointsBySection: Map<number, number>
}

export const ScheduleControls = ({
  setScheduleSaving,
  manualSequence,
  setManualSequence,
  loadManualSequence,
  markManualSequenceSaved,
  manualSequenceDirty,
  isSequencingMode,
  setIsSequencingMode,
  selectedSectionId,
  setSelectedSectionId,
  sections,
  setSections,
  scheduledPointsBySection,
}: IScheduleControlsProps) => {
  const { updateSchedule, state: buildingState } = useBuildingContext()

  const { canCreateSchedules, canEditSchedules } = useManageSchedulesContext()

  const { navigateToManageSchedules } = useNavigation()

  const { addNotification } = useNotifications()

  const [confirmation, setConfirmation] = useState<boolean>(false)
  const [confirmationMessage, setConfirmationMessage] = useState<string>('')
  const [sectionAssignmentConfirmation, setSectionAssignmentConfirmation] =
    useState<boolean>(false)
  const [hasConfirmedSectionAssignment, setHasConfirmedSectionAssignment] =
    useState<boolean>(false)
  const [fieldValidations, updateFieldValidations] = useReducer(
    fieldValidationsReducer,
    initialFieldValidations,
  )

  const { scheduleData } = buildingState
  const { name, start_date, till_date, time, frequency, id } = scheduleData
  const {
    lessThan24HoursAway: nextScanLessThan24HoursAway,
    isExpired: scanExpired,
  } = getNextScan(frequency, start_date, till_date, time)

  const isNewSchedule = id === 'new'

  const canEditScheduleFields = isNewSchedule
    ? canCreateSchedules
    : canEditSchedules

  useFetchProjectFloorSectionsQuery(
    buildingState.projectId,
    buildingState.floorId,
    (data: ProjectFloorSection[]) => {
      setSections(data)
      if (data.length > 0) {
        setSelectedSectionId(prev =>
          prev != null && data.some(section => section.id === prev)
            ? prev
            : data[0].id,
        )
      }
    },
  )

  useEffect(() => {
    if (isNewSchedule || selectedSectionId == null) {
      if (isNewSchedule) {
        loadManualSequence([])
      }
      return
    }

    let cancelled = false

    const fetchSequence = async () => {
      try {
        const response = await fetchScheduleManualSequence(
          buildingState.projectId,
          buildingState.scheduleId,
          selectedSectionId,
        )

        if (cancelled) {
          return
        }

        const loadedSequence = Array.isArray(response?.point_sequence)
          ? response.point_sequence
          : []
        loadManualSequence(loadedSequence)
      } catch {
        if (!cancelled) {
          loadManualSequence([])
        }
      }
    }

    void fetchSequence()

    return () => {
      cancelled = true
    }
  }, [
    isNewSchedule,
    selectedSectionId,
    buildingState.projectId,
    buildingState.scheduleId,
    loadManualSequence,
  ])

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateSchedule({
      name: e.target.value,
    })

    updateFieldValidations({
      name: true,
    })
  }

  const onChangeFrequency = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateSchedule({
      frequency: e.target.value,
    })

    updateFieldValidations({
      frequency: true,
    })
  }

  const onChangeTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedStartDate = start_date
    const updatedTillDate = till_date
    const updatedTime = e.target.value

    if (updatedTime) {
      updateFieldValidations({
        time: true,
      })

      const timePieces = getTimePieces(updatedTime)

      updatedStartDate.setHours(timePieces[0], timePieces[1])
      updatedTillDate.setHours(timePieces[0], timePieces[1])
    }

    updateSchedule({
      time: updatedTime,
      start_date: updatedStartDate,
      till_date: updatedTillDate,
    })
  }

  const onResetManualSequence = () => {
    setManualSequence([])
  }

  const validateManualSequenceForSave = (
    sequence: number[],
    requiredPointIds: number[],
  ): string | null => {
    if (sequence.length === 0) {
      return null
    }

    const sequenceSet = new Set(sequence)
    if (sequenceSet.size !== sequence.length) {
      return 'Manual sequence contains duplicate point ids. Please fix and try again.'
    }

    const includesAllRequiredPoints =
      sequence.length === requiredPointIds.length &&
      requiredPointIds.every(pointId => sequenceSet.has(pointId))

    if (!includesAllRequiredPoints) {
      return 'Manual sequence must include each scheduled point in the selected section exactly once. Finish the sequence or reset before saving.'
    }

    return null
  }

  const onSaveSchedule = async () => {
    const saveDisabled =
      !name ||
      !start_date ||
      !till_date ||
      !frequency ||
      frequency === Frequency.never ||
      !time ||
      nextScanLessThan24HoursAway

    if (saveDisabled) {
      if (scanExpired) {
        setConfirmationMessage(
          `Cannot ${isNewSchedule ? 'create' : 'save'} a schedule that is expired`,
        )
      } else if (nextScanLessThan24HoursAway) {
        setConfirmationMessage(
          `Cannot ${isNewSchedule ? 'create' : 'save'} a schedule for less than 24 hours from now`,
        )
      } else {
        setConfirmationMessage(
          `Please fill all fields to ${isNewSchedule ? 'create new' : 'save'} schedule`,
        )
      }

      setConfirmation(true)

      updateFieldValidations({
        name: !!name,
        start_date: !!start_date,
        till_date: !!till_date,
        frequency: !!frequency && frequency !== Frequency.never,
        time: !!time,
      })

      return
    }

    const formData = {
      id,
      name,
      frequency,
      start_date: formatDateForSubmit(start_date),
      till_date: formatDateForSubmit(till_date),
      time,
    }

    if (isNewSchedule) {
      const selectedPointIds: number[] = Array.from(
        buildingState.floorData.selectedPoints,
      )
      const selectedPointIdSet = new Set(selectedPointIds)
      const selectedPointsData: IMapPoint[] =
        buildingState.floorData.points.filter((point: IMapPoint) =>
          selectedPointIdSet.has(point.point_id),
        )
      const pointsMissingSection = selectedPointsData.filter(
        (point: IMapPoint) => point.project_floor_section == null,
      )

      if (pointsMissingSection.length > 0) {
        if (sections.length === 0) {
          setConfirmationMessage(
            'Selected points are missing a section, but this floor has no sections to assign.',
          )
          setConfirmation(true)
          return
        }

        if (selectedSectionId == null || !hasConfirmedSectionAssignment) {
          setSectionAssignmentConfirmation(true)
          return
        }
      }

      if (manualSequence.length > 0) {
        const sequenceError = validateManualSequenceForSave(
          manualSequence,
          selectedPointIds,
        )
        if (sequenceError) {
          setConfirmationMessage(sequenceError)
          setConfirmation(true)
          return
        }
      }

      try {
        setScheduleSaving(true)

        let pointsForSchedule = selectedPointsData
        if (pointsMissingSection.length > 0 && selectedSectionId != null) {
          const updatedPoints = await Promise.all(
            pointsMissingSection.map(point =>
              updatePoint(
                buildingState.projectId,
                buildingState.floorId,
                point.point_id,
                {
                  project_floor_section: selectedSectionId,
                },
              ),
            ),
          )
          const updatedPointMap = new Map<number, IMapPoint>(
            updatedPoints.map(point => [point.point_id, point]),
          )
          pointsForSchedule = selectedPointsData.map(
            point => updatedPointMap.get(point.point_id) ?? point,
          )
        }

        const createdSchedule = await createNewSchedule(
          buildingState.projectId,
          formData,
        )

        await Promise.all(
          selectedPointIds.map((pointId: number) =>
            createSchedulePointMapping(
              buildingState.projectId,
              createdSchedule.id,
              pointId,
            ),
          ),
        )

        if (manualSequence.length > 0) {
          const pointsBySection = new Map<number, Set<number>>()
          pointsForSchedule.forEach(point => {
            if (point.project_floor_section != null) {
              const sectionPointSet = pointsBySection.get(
                point.project_floor_section,
              )
              if (sectionPointSet) {
                sectionPointSet.add(point.point_id)
              } else {
                pointsBySection.set(
                  point.project_floor_section,
                  new Set([point.point_id]),
                )
              }
            }
          })

          await Promise.all(
            Array.from(pointsBySection.entries()).map(
              ([projectFloorSectionId, sectionPointSet]) => {
                const sectionSequence = manualSequence.filter(pointId =>
                  sectionPointSet.has(pointId),
                )
                if (sectionSequence.length === 0) {
                  return Promise.resolve()
                }
                return createScheduleManualSequence(
                  buildingState.projectId,
                  createdSchedule.id,
                  projectFloorSectionId,
                  sectionSequence,
                )
              },
            ),
          )
        }

        setScheduleSaving(false)
        setHasConfirmedSectionAssignment(false)
        navigateToManageSchedules(buildingState.floorId, createdSchedule.id)
        addNotification('Schedule created successfully', 'success')
      } catch (err) {
        setScheduleSaving(false)
        // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
        console.log('createNewSchedule==>>', err)
        addNotification('Error creating new schedule', 'error')
      }
    } else {
      try {
        if (selectedSectionId != null && manualSequenceDirty) {
          const scheduledPointIdsOnFloor =
            buildingState.scheduleMappings.get(buildingState.floorId) ??
            new Set<number>()
          const selectedPointsOnFloor: IMapPoint[] =
            buildingState.floorData.points.filter((point: IMapPoint) =>
              scheduledPointIdsOnFloor.has(point.point_id),
            )
          const sectionPointIds = pointIdsForSection(
            selectedPointsOnFloor,
            selectedSectionId,
          )

          const sequenceError = validateManualSequenceForSave(
            manualSequence,
            sectionPointIds,
          )
          if (sequenceError) {
            setConfirmationMessage(sequenceError)
            setConfirmation(true)
            return
          }
        } else if (
          manualSequenceDirty &&
          manualSequence.length > 0 &&
          selectedSectionId == null
        ) {
          setConfirmationMessage(
            'Please choose a section before saving a manual sequence.',
          )
          setConfirmation(true)
          return
        }

        const savedSchedule = await saveSchedule(
          buildingState.projectId,
          formData,
        )

        const shouldSaveManualSequence =
          selectedSectionId != null && manualSequenceDirty

        if (shouldSaveManualSequence && selectedSectionId != null) {
          await createScheduleManualSequence(
            buildingState.projectId,
            savedSchedule.id,
            selectedSectionId,
            manualSequence,
          )
          markManualSequenceSaved()
        }

        updateSchedule(savedSchedule)
        addNotification(
          shouldSaveManualSequence
            ? 'Schedule and manual sequence saved successfully'
            : 'Schedule saved successfully',
          'success',
        )
      } catch (err) {
        // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
        console.log('saveSchedule==>>', err)
        addNotification('Error saving schedule', 'error')
      }
    }
  }

  return (
    <ScheduleControlsContainer>
      <InputContainer>
        <NameInput
          placeholder="Name"
          value={name}
          onChange={onChangeName}
          valid={fieldValidations.name}
          disabled={!canEditScheduleFields}
        />
      </InputContainer>
      <InputContainer style={{ position: 'relative' }}>
        <CustomDropdownArrow />
        <FrequencyDropdown
          value={frequency}
          onChange={onChangeFrequency}
          valid={fieldValidations.frequency}
          disabledStyle={!canEditScheduleFields}
        >
          <option disabled hidden value={Frequency.never}>
            {Frequency.never}
          </option>
          <EnabledFrequencyOption value={Frequency.once}>
            {Frequency.once}
          </EnabledFrequencyOption>
          <EnabledFrequencyOption value={Frequency.weekly}>
            {Frequency.weekly}
          </EnabledFrequencyOption>
          <EnabledFrequencyOption value={Frequency.monthly}>
            {Frequency.monthly}
          </EnabledFrequencyOption>
        </FrequencyDropdown>
      </InputContainer>

      <InputContainer>
        <ScheduleControlsDatePicker
          valid={fieldValidations.start_date && fieldValidations.till_date}
          disabled={!canEditScheduleFields}
        />
      </InputContainer>

      <InputContainer>
        <TimeInput
          value={time}
          onChange={onChangeTime}
          valid={fieldValidations.time}
          disabled={!canEditScheduleFields}
        />
      </InputContainer>

      {canEditScheduleFields && sections.length > 0 && (
        <InputContainer style={{ position: 'relative' }}>
          <CustomDropdownArrow />
          <StyledSelect
            value={selectedSectionId ?? ''}
            onChange={e => setSelectedSectionId(Number(e.target.value))}
          >
            {sections.map(section => {
              const scheduledCount =
                scheduledPointsBySection.get(section.id) ?? 0
              return (
                <option key={section.id} value={section.id}>
                  Sequence section: {section.name} ({scheduledCount} scheduled)
                </option>
              )
            })}
          </StyledSelect>
        </InputContainer>
      )}

      {canEditScheduleFields && (
        <InputContainer>
          <ManualSequenceLabel>Manual point sequence</ManualSequenceLabel>
          <ManualSequenceActions>
            <SequenceModeButton
              type="button"
              $active={isSequencingMode}
              onClick={setIsSequencingMode}
            >
              {isSequencingMode ? 'Exit Sequence' : 'Create Sequence'}
            </SequenceModeButton>
            <ResetSequenceButton
              type="button"
              onClick={onResetManualSequence}
              disabled={manualSequence.length === 0}
            >
              Reset
            </ResetSequenceButton>
          </ManualSequenceActions>
          {isSequencingMode && (
            <ManualSequenceHelpText as="ol">
              <li>
                Select a sequence section with scheduled points (points are
                pink)
              </li>
              <li>Click points in the order of your manual sequence</li>
              <li>
                Click Reset to clear point path or{' '}
                {isNewSchedule ? 'Create Schedule' : 'Save'} to save sequence
              </li>
            </ManualSequenceHelpText>
          )}
        </InputContainer>
      )}

      {canEditScheduleFields && (
        <SaveButton onClick={onSaveSchedule}>
          {isNewSchedule ? 'Create Schedule' : 'Save'}
        </SaveButton>
      )}

      <ScheduleControlsStatistics />

      <ScheduleControlsScanInformation />

      <ConfirmationModal
        hideCancelButton
        confirmButtonAltText="Ok"
        isOpen={confirmation}
        setIsOpen={setConfirmation}
        message={confirmationMessage}
      />
      <ConfirmationModal
        isOpen={sectionAssignmentConfirmation}
        setIsOpen={setSectionAssignmentConfirmation}
        message="Some selected points are not assigned to a section. Choose a section to assign before creating this schedule."
        confirmButtonAltText="Assign & Create"
        onConfirm={() => {
          setHasConfirmedSectionAssignment(true)
          void onSaveSchedule()
        }}
        content={
          <StyledSelect
            value={selectedSectionId ?? ''}
            onChange={e => {
              setSelectedSectionId(Number(e.target.value))
              setHasConfirmedSectionAssignment(false)
            }}
          >
            {sections.map(section => (
              <option key={section.id} value={section.id}>
                {section.name}
              </option>
            ))}
          </StyledSelect>
        }
      />
    </ScheduleControlsContainer>
  )
}

interface IScheduleControlsInputProps {
  valid: boolean
  disabledStyle?: boolean
}

const ScheduleControlsContainer = styled.div`
  padding: 16px 16px 16px 12px;
`

const InputContainer = styled.div`
  margin-bottom: 12px;
`

const NameInput = styled.input<IScheduleControlsInputProps>`
  ${commonInputStyles}
  ${props => (!props.valid ? invalidInputStyle : '')}

  &:disabled {
    background-color: rgba(239, 239, 239, 0.3);
    color: rgb(84, 84, 84);
  }
`

const TimeInput = styled.input.attrs({
  type: 'time',
})<IScheduleControlsInputProps>`
  ${commonInputStyles}
  padding: 5px 10px;
  ${props => (!props.valid ? invalidInputStyle : '')}

  &:disabled {
    background-color: rgba(239, 239, 239, 0.3);
    color: rgb(84, 84, 84);
  }
`

const ManualSequenceLabel = styled.label`
  display: block;
  font-size: 12px;
  line-height: 15px;
  color: #64748b;
  margin-bottom: 6px;
`

const ManualSequenceHelpText = styled.ol`
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 11px;
  line-height: 1.45;
  color: #64748b;

  li {
    margin-bottom: 4px;
  }

  li:last-child {
    margin-bottom: 0;
  }
`

const ManualSequenceActions = styled.div`
  display: flex;
  gap: 8px;
`

const SequenceModeButton = styled.button<{ $active: boolean }>`
  border: 1px solid ${props => (props.$active ? '#073c7a' : '#94a3b8')};
  border-radius: 2px;
  font-size: 12px;
  line-height: 15.3px;
  font-weight: 700;
  color: ${props => (props.$active ? '#ffffff' : '#073c7a')};
  background-color: ${props => (props.$active ? '#073c7a' : 'transparent')};
  padding: 8px 10px;
  cursor: pointer;
  flex: 1;
`

const ResetSequenceButton = styled.button`
  border: 1px solid #94a3b8;
  border-radius: 2px;
  font-size: 12px;
  line-height: 15.3px;
  font-weight: 700;
  color: #64748b;
  background-color: transparent;
  padding: 8px 10px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

export const StyledSelect = styled.select`
  ${commonInputStyles}
  appearance: none;
  background: transparent;
  padding-right: 32px;

  &:disabled {
    background-color: rgba(239, 239, 239, 0.3);
    color: rgb(84, 84, 84);
  }
`

const FrequencyDropdown = styled.select<IScheduleControlsInputProps>`
  ${commonInputStyles}
  background: transparent;
  appearance: none;
  padding-right: 32px;
  ${props => (!props.valid ? invalidInputStyle : '')}
  ${props => (props.value === Frequency.never ? 'color: gray;' : '')}

  ${props =>
    props.disabledStyle &&
    `
    background-color: rgba(239, 239, 239, 0.3);
    color: rgb(84, 84, 84);
  `}
`

const EnabledFrequencyOption = styled.option`
  color: black;
`

const SaveButton = styled.div`
  border: 1px solid #073c7a;
  border-radius: 2px;
  font-size: 12px;
  line-height: 15.3px;
  font-weight: 700;
  color: #073c7a;
  padding: 8px 10px;
  text-align: center;
  cursor: pointer;
`

export const CustomDropdownArrow = () => {
  return (
    <img
      src={iconDown}
      style={{
        position: 'absolute',
        right: '14px',
        top: '10px',
        pointerEvents: 'none',
      }}
      alt="expand date picker"
    />
  )
}
